import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RouterState, push } from 'connected-react-router';

import {
  InitializeState,
  InitializeStatus
} from 'common/modules/app/initialize';
import { GlossaryState } from 'common/modules/app/glossaries';
import { UserInformation } from 'common/modules/domain/user';
import {
  getPlan,
  PlanStateType,
  ServiceIdType,
  GET_PLAN_ID,
  PlanInformation,
  PlanInitialState
} from 'common/modules/domain/plan';
import {
  ErrorState,
  notFoundPurchasePlan,
  specifiedInvalidUrl
} from 'common/modules/app/errors';
import { FetchState, FetchStatus } from 'common/modules/app/fetch';
import { InnerError } from 'common/modules/app/innerErrorCode';
import { Glossary, PlanDetailGL } from 'common/utils/glossary';
import { SasPagePath } from 'common/utils/urls';
import { AppState } from 'pages';
import { Title, Spinner } from 'common/components';

import { default as Services } from './Services';
import { Plan } from '../components/Plan';
import {
  deleteCookie,
  loadCookie,
  SasCookieKeys
} from 'common/utils/cookie-utils';
import { default as PlanDetailMessage } from 'pages/plan-detail/containers/PlanDetailMessage';

const _isPlanNotFound = (errorState: ErrorState) => {
  return errorState.requestId === GET_PLAN_ID && errorState.statusCode === 400;
};

/**
 * StoreのStateからComponentに渡すPropsへ
 */
const useStateProps = () => {
  const initState = useSelector<AppState, InitializeState>(
    (state) => state.app.initialize
  );
  const glState = useSelector<AppState, GlossaryState>(
    (state) => state.app.glossaries
  );
  const planState = useSelector<AppState, PlanInitialState>(
    (state) => state.domain.plan
  );
  const userState = useSelector<AppState, UserInformation>(
    (state) => state.domain.user
  );
  const routerState = useSelector<AppState, RouterState>(
    (state) => state.app.router
  );
  const errorState = useSelector<AppState, ErrorState>(
    (state) => state.app.error
  );
  const fetchState = useSelector<AppState, FetchState>(
    (state) => state.app.fetch
  );
  const isPlanProcessing =
    fetchState[GET_PLAN_ID] !== null &&
    fetchState[GET_PLAN_ID] === FetchStatus.PROCESSING;
  return {
    init: initState,
    glossary: Glossary.instance(glState),
    plan: planState,
    user: userState,
    router: routerState,
    isProcessingPlan: isPlanProcessing,
    isPlanNotFound: _isPlanNotFound(errorState)
  };
};

type SelectedState = ReturnType<typeof useStateProps>;
/**
 * StoreのStateとActionをdispatchする関数からComponentに渡すPropsへ
 */
const useMargeProps = (state: SelectedState) => {
  const dispatch = useDispatch();
  const cookie = loadCookie();
  // routerStateからクエリパラメータを取得する
  const _searchParams = new URLSearchParams(state.router.location.search);
  const _planId = _searchParams.get('planId') || '';
  const _serviceId =
    _searchParams.get('serviceId') || ServiceIdType.TYPE_ALL_PLAN;
  const _country = state.user.country;
  const _language = _searchParams.get('language') || state.user.language;
  // cookieから取得する
  const _modelName = cookie[SasCookieKeys.KEY_MODEL_NAME];
  const _type = cookie[SasCookieKeys.KEY_TYPE];

  /** プラン情報に税込価格が含まれているかどうか */
  const hasTaxIncludedPrice = (plan: PlanInformation | null) => {
    if (
      plan &&
      plan.prices.length &&
      plan.prices[0].taxIncludedPrice !== null &&
      plan.prices[0].taxIncludedPrice !== undefined
    ) {
      return true;
    }
    return false;
  };

  /**
   * 指定されたプランIDのプラン情報を設定する。
   * ただし税込価格を持たないプラン情報だった場合、それを渡さず、初期化時にプラン一件取得を実行させる。
   * （プラン一覧取得APIには税込価格だけ入ってこない。一度はプラン一件取得が必要。）
   */
  const targetPlan = hasTaxIncludedPrice(state.plan.planList[_planId])
    ? state.plan.planList[_planId]
    : null;

  /** 初期化処理 */
  const onInit = () => {
    // 一覧からそのプランの詳細画面を初めて開いたとき、または詳細画面へ直接遷移したとき、
    // プラン一件取得APIを実行してプラン情報を取得する。
    if (_planId && !targetPlan) {
      dispatch(
        getPlan(
          _planId,
          _country,
          _language,
          PlanStateType.TYPE_ACTIVE,
          _serviceId,
          _modelName,
          _type
        )
      );
    }
    // クエリパラメータ未指定又はplanId未指定の場合はエラーページに遷移する
    if (!_planId) {
      handleNotExistPlan();
    }
    // 別画面からの遷移時に、遷移元のスクロール位置が反映されてしまうのを防ぐため、位置をリセット
    window.scrollTo(0, 0);
  };

  /** クエリパラメータのプランが取得できなかった場合の処理 */
  const handleNotExistPlan = () => {
    // 未ログイン状態で購入ボタンを押下し、ログインしたが国が対応していない場合
    // 購入確認画面表示用のクッキーを消す
    const cookie = loadCookie();
    deleteCookie(SasCookieKeys.KEY_BEFORE_PURCHASE);
    if (cookie[SasCookieKeys.KEY_BEFORE_PURCHASE]) {
      // 未ログインで購入ボタンを押下し、ログイン後に遷移したケースの場合
      const error = InnerError.planDetailNotFoundPurchasePlan;
      dispatch(notFoundPurchasePlan(error));
      dispatch(push(SasPagePath.Error));
    } else {
      // 直接URLを指定して遷移した場合
      const error = InnerError.planDetailSpecifiedInvalidUrl;
      dispatch(specifiedInvalidUrl(error));
      dispatch(push(SasPagePath.Error));
    }
  };

  return {
    ...state,
    targetPlan: targetPlan,
    onInit: onInit,
    handleNotExistPlan: handleNotExistPlan,
    isProcessingPlan: state.isProcessingPlan
  };
};

/**
 * プラン詳細画面全体のContainer Component
 */
export default function ContainerComponent() {
  const _props = { ...useMargeProps(useStateProps()) };
  const isCompleteInit = _props.init.status === InitializeStatus.FINISH;

  // 初期化処理イベント
  React.useEffect(() => {
    isCompleteInit && _props.onInit();
  }, [isCompleteInit]);

  // 存在しないプランIDの場合のイベント
  React.useEffect(() => {
    _props.isPlanNotFound && _props.handleNotExistPlan();
  }, [_props.isPlanNotFound]);

  const _component = React.useMemo(
    () =>
      _props.targetPlan && Object.keys(_props.targetPlan).length ? (
        <>
          <Plan targetPlan={_props.targetPlan} />
          <Services targetPlan={_props.targetPlan} />
        </>
      ) : _props.isProcessingPlan ? (
        <Spinner />
      ) : null,
    [_props.targetPlan, _props.isProcessingPlan]
  );

  return (
    <section id="page" data-test="planDetailPageSection">
      <div className="planDetails" data-test="planDetailPage">
        <Title type="heading" data-test="planDetailPageTitle">
          {_props.glossary.glx(PlanDetailGL.DetailTitle)}
        </Title>
        <PlanDetailMessage />
        {_component}
      </div>
    </section>
  );
}
